export const DOCS_SECTIONS = [
  {
    title: "General",
    items: [
      { id: "overview", name: "Overview", path: "/overview" },
      { id: "authentication", name: "Authentication", path: "/authentication" },
      { id: "signatures", name: "Signature Validation", path: "/signatures" },
      { id: "testing", name: "Testing", path: "/testing" },
    ],
  },
  {
    title: "API Reference",
    items: [
      { id: "create-payment", name: "Create Payment Link", path: "/api/create-payment" },
      { id: "get-payment-status", name: "Get Payment Status", path: "/api/get-payment-status" },
      { id: "cancel-payment", name: "Cancel Payment", path: "/api/cancel-payment" },
      { id: "refunds", name: "Refunds", path: "/api/refunds" },
      { id: "webhook", name: "Webhook Notification", path: "/api/webhook" },
    ],
  },
];

export const DOCS_CONTENT = {
  overview: `# Dashboard Overview

Welcome to the **PAYD API Documentation**. This platform provides everything you need to integrate our payment solutions into your application.

## Quick Start Guide

Get up and running with PAYD in minutes. Choose your integration path below:

<Cards>
  <Cards.Card
    title="Create Payment Link"
    href="/api/create-payment"
  />
  <Cards.Card
    title="Check Status"
    href="/api/get-payment-status"
  />
  <Cards.Card
    title="Webhooks"
    href="/api/webhook"
  />
</Cards>

## Integration Resources

- **Single Host API**: All requests are executed on the same API host.
- **Test Mode**: Test mode vs live mode is determined directly by the API key used for your request.
- **SDKs & Libraries**: Coming soon for Node.js, Python, and PHP.

---

Need help? Contact our support team at [support@payd.ae](mailto:support@payd.ae) or visit our [Help Center](https://payd.ae/help).
`,

  authentication: `# Authentication

The PAYD API uses API keys to authenticate requests. You can manage your API keys in the [PAYD Merchant Dashboard](https://payd.ae/dashboard).

## API Keys

Every request to the PAYD API must include an API key in the request headers. We support two types of keys:

- **Public Key**: Used for client-side integrations (e.g., in your frontend code).
- **Secret Key**: Used for server-to-server communication. **Never share your secret key or commit it to a public repository.**

## Authentication Header

Include your API key in the \`Authorization\` header as a Bearer token:

\`\`\`http
Authorization: Bearer YOUR_API_KEY
\`\`\`

Alternatively, you can use the custom \`X-API-KEY\` header:

\`\`\`http
X-API-KEY: YOUR_API_KEY
\`\`\`

## Environments & Test Mode

PAYD operates on a **Single Host Architecture** (\`\${REACT_APP_PAYD_API_BASE_URL}\`). There is no separate sandbox domain or host URL.

Whether a transaction runs in **Test Mode** or **Live Mode** is determined directly by the API Key (\`X-Api-Key\`) used for the request.

- **Test Mode**: Use your test API key to test integrations and simulate payments safely without moving real funds.
- **Live Mode**: Use your live API key to accept real production payments.
- **API Base URL**: \`\${REACT_APP_PAYD_API_BASE_URL}/api/v1\`

## Security Best Practices

- Always use HTTPS for API requests.
- Rotate your API keys regularly if you suspect they have been compromised.
- Use environment variables to store your keys in your server-side applications.
`,

  signatures: `# Signature Validation

Signature validation ensures that API responses and webhook callbacks genuinely originated from PAYD and have not been altered or tampered with by any third party in transit.

Every API server response and outgoing webhook callback from PAYD includes an \`X-Payd-Signature\` HTTP header containing a timestamped cryptographic HMAC-SHA256 signature.

<Callout type="info">
  Signature validation is powered by your Secret API Key (\`sk_live_...\` or \`sk_test_...\`). Because only you and PAYD possess your secret key, third parties cannot forge valid signatures.
</Callout>

---

## The \`X-Payd-Signature\` Header

The signature is sent in every response and callback under the \`X-Payd-Signature\` header:

\`\`\`http
X-Payd-Signature: t=1722933706,v1=a3f8902c34d8e9f...
\`\`\`

| Parameter | Type | Description |
| :--- | :--- | :--- |
| \`t\` | \`integer\` | Unix timestamp (in seconds) indicating when PAYD signed the payload. |
| \`v1\` | \`string\` | The HMAC-SHA256 hex digest generated using your Secret API Key. |

---

## Signed Payload Formats

### 1. API Server Responses
For responses returned by PAYD API endpoints (e.g. \`/v1/payment/create\`, \`/v1/payment/status\`), the signed string is constructed as:

\`\`\`text
t={timestamp}.{raw_response_body}
\`\`\`

### 2. Outgoing Webhook Callbacks
For webhook notification requests delivered to your server's callback URL, the signed string is constructed as:

\`\`\`text
t={timestamp}.{full_callback_url_with_query_params}
\`\`\`

---

## Verification Steps

To verify incoming signatures, perform the following 4 steps on your server:

1. **Extract \`t\` and \`v1\`**: Parse the \`X-Payd-Signature\` header by splitting on \`,\` and \`=\`.
2. **Check Timestamp Tolerance**: Ensure \`|t_current - t_received| <= 300 seconds\` (5 minutes) to protect against replay attacks.
3. **Compute HMAC-SHA256**: Generate the expected HMAC-SHA256 hex digest using your Secret API Key.
4. **Timing-Safe Comparison**: Compare your computed hash with \`v1\` using a constant-time comparison function.

<Callout type="warning">
  Always use **raw response strings** exactly as received over the network. Re-serializing or pretty-printing JSON payloads will alter the payload format and cause signature verification to fail.
</Callout>

---

## Integration Code Examples

Select your preferred language below to view ready-to-use verification functions:

<Tabs items={['Node.js / TypeScript', 'PHP / Laravel', 'Python', 'Go', 'C# / .NET']}>
  <Tabs.Tab>
    \`\`\`typescript
    import * as crypto from 'crypto';

    interface VerifyParams {
      signatureHeader: string;
      bodyOrUrl: string;
      apiKey: string;
      toleranceSeconds?: number;
    }

    export function verifyPaydSignature({
      signatureHeader,
      bodyOrUrl,
      apiKey,
      toleranceSeconds = 300
    }: VerifyParams): boolean {
      if (!signatureHeader) return false;

      // 1. Parse header
      const parts = Object.fromEntries(
        signatureHeader.split(',').map(item => item.split('='))
      );
      const timestamp = parts['t'];
      const receivedSig = parts['v1'];

      if (!timestamp || !receivedSig) return false;

      // 2. Check timestamp tolerance (Anti-Replay)
      const now = Math.floor(Date.now() / 1000);
      if (Math.abs(now - parseInt(timestamp, 10)) > toleranceSeconds) {
        return false;
      }

      // 3. Re-compute HMAC-SHA256
      const payloadToSign = \`t=\${timestamp}.\${bodyOrUrl}\`;
      const expectedSig = crypto
        .createHmac('sha256', apiKey)
        .update(payloadToSign, 'utf8')
        .digest('hex');

      // 4. Timing-safe comparison
      const expectedBuffer = Buffer.from(expectedSig, 'hex');
      const receivedBuffer = Buffer.from(receivedSig, 'hex');

      return expectedBuffer.length === receivedBuffer.length &&
        crypto.timingSafeEqual(expectedBuffer, receivedBuffer);
    }
    \`\`\`
  </Tabs.Tab>

  <Tabs.Tab>
    \`\`\`php
    <?php

    function verifyPaydSignature(
        string $signatureHeader,
        string $bodyOrUrl,
        string $apiKey,
        int $toleranceSeconds = 300
    ): bool {
        if (empty($signatureHeader)) {
            return false;
        }

        // 1. Parse header parameters
        parse_str(str_replace(',', '&', $signatureHeader), $params);
        $timestamp = $params['t'] ?? null;
        $receivedSig = $params['v1'] ?? null;

        if (!$timestamp || !$receivedSig) {
            return false;
        }

        // 2. Check timestamp tolerance (Anti-Replay)
        if (abs(time() - (int)$timestamp) > $toleranceSeconds) {
            return false;
        }

        // 3. Re-compute HMAC-SHA256
        $payloadToSign = "t={$timestamp}.{$bodyOrUrl}";
        $expectedSig = hash_hmac('sha256', $payloadToSign, $apiKey);

        // 4. Timing-safe comparison
        return hash_equals($expectedSig, $receivedSig);
    }
    \`\`\`
  </Tabs.Tab>

  <Tabs.Tab>
    \`\`\`python
    import hmac
    import hashlib
    import time

    def verify_payd_signature(signature_header: str, body_or_url: str, api_key: str, tolerance_sec: int = 300) -> bool:
        if not signature_header:
            return False

        # 1. Parse header
        try:
            parts = dict(item.split('=', 1) for item in signature_header.split(','))
            timestamp = parts.get('t')
            received_sig = parts.get('v1')
        except ValueError:
            return False

        if not timestamp or not received_sig:
            return False

        # 2. Check timestamp tolerance (Anti-Replay)
        if abs(time.time() - int(timestamp)) > tolerance_sec:
            return False

        # 3. Re-compute HMAC-SHA256
        payload_to_sign = f"t={timestamp}.{body_or_url}".encode('utf-8')
        expected_sig = hmac.new(
            api_key.encode('utf-8'),
            payload_to_sign,
            hashlib.sha256
        ).hexdigest()

        # 4. Timing-safe comparison
        return hmac.compare_digest(expected_sig, received_sig)
    \`\`\`
  </Tabs.Tab>

  <Tabs.Tab>
    \`\`\`go
    package main

    import (
    	"crypto/hmac"
    	"crypto/sha256"
    	"encoding/hex"
    	"fmt"
    	"math"
    	"strconv"
    	"strings"
    	"time"
    )

    func VerifyPaydSignature(signatureHeader, bodyOrUrl, apiKey string, toleranceSec int64) bool {
    	if signatureHeader == "" {
    		return false
    	}

    	// 1. Parse header
    	params := make(map[string]string)
    	for _, pair := range strings.Split(signatureHeader, ",") {
    		kv := strings.SplitN(pair, "=", 2)
    		if len(kv) == 2 {
    			params[kv[0]] = kv[1]
    		}
    	}

    	timestampStr, okT := params["t"]
    	receivedSig, okV := params["v1"]
    	if !okT || !okV {
    		return false
    	}

    	// 2. Check timestamp tolerance
    	timestamp, err := strconv.ParseInt(timestampStr, 10, 64)
    	if err != nil || math.Abs(float64(time.Now().Unix()-timestamp)) > float64(toleranceSec) {
    		return false
    	}

    	// 3. Re-compute HMAC-SHA256
    	mac := hmac.New(sha256.New, []byte(apiKey))
    	mac.Write([]byte(fmt.Sprintf("t=%s.%s", timestampStr, bodyOrUrl)))
    	expectedSig := hex.EncodeToString(mac.Sum(nil))

    	// 4. Timing-safe comparison
    	return hmac.Equal([]byte(expectedSig), []byte(receivedSig))
    }
    \`\`\`
  </Tabs.Tab>

  <Tabs.Tab>
    \`\`\`csharp
    using System;
    using System.Collections.Generic;
    using System.Security.Cryptography;
    using System.Text;

    public class PaydSecurity
    {
        public static bool VerifyPaydSignature(string signatureHeader, string bodyOrUrl, string apiKey, int toleranceSeconds = 300)
        {
            if (string.IsNullOrEmpty(signatureHeader)) return false;

            // 1. Parse header
            var paramsMap = new Dictionary<string, string>();
            foreach (var pair in signatureHeader.Split(','))
            {
                var kv = pair.Split('=');
                if (kv.Length == 2) paramsMap[kv[0]] = kv[1];
            }

            if (!paramsMap.TryGetValue("t", out var timestampStr) || !paramsMap.TryGetValue("v1", out var receivedSig))
                return false;

            // 2. Check timestamp tolerance
            if (!long.TryParse(timestampStr, out var timestamp) || Math.Abs(DateTimeOffset.UtcNow.ToUnixTimeSeconds() - timestamp) > toleranceSeconds)
                return false;

            // 3. Re-compute HMAC-SHA256
            var payloadToSign = $"t={timestampStr}.{bodyOrUrl}";
            using var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(apiKey));
            var hashBytes = hmac.ComputeHash(Encoding.UTF8.GetBytes(payloadToSign));
            var expectedSig = Convert.ToHexString(hashBytes).ToLower();

            // 4. Timing-safe comparison
            return CryptographicOperations.FixedTimeEquals(
                Encoding.UTF8.GetBytes(expectedSig),
                Encoding.UTF8.GetBytes(receivedSig)
            );
        }
    }
    \`\`\`
  </Tabs.Tab>
</Tabs>
`,

  testing: `# Testing

PAYD operates on a **Single Host Architecture** (\${REACT_APP_PAYD_API_BASE_URL}). There is no separate sandbox host or environment URL. Whether requests run in **Test Mode** or **Live Mode** is determined directly by the API Key (\`X-Api-Key\`) provided.

## Test API Keys & Endpoints

Include your Test API Key in the \`X-Api-Key\` header for all test requests targeting:

\`\${REACT_APP_PAYD_API_BASE_URL}/api/v1\`

## Test Card Numbers

You can use the following test card numbers to perform test payments:

| Card Number | Expiry | CVV | Expected Outcome |
| :--- | :--- | :--- | :--- |
| \`5123450000000008\` | \`12/29\` | \`123\` | **Success** |
| \`4005550000000001\` | \`05/30\` | \`123\` | **Success** |

## Simulating Payment Statuses

When testing the \`GET /payment/status\` endpoint, you can simulate different statuses by using specific \`invoiceId\` patterns or amounts (depending on the gateway configuration):

- **Pending**: Payment link created but not yet opened.
- **Completed**: Successfully paid.
- **Failed**: Transaction rejected by the gateway.
- **Canceled**: Payment link manually canceled by the merchant or customer.

## Webhook Testing

To test webhooks:
1. Use a service like [Webhook.site](https://webhook.site) to get a temporary URL.
2. Set this URL as your \`callbackUrl\` in the \`create-payment\` request.
3. Complete a test transaction using a success test card.
4. Verify that your endpoint receives the \`POST\` notification.
`,

  "create-payment": `# Create Payment Link

Creates a payment link with the provided details. This is the primary endpoint for initiating a transaction.

<Callout type="warning">
  The \`amount\` field must be represented in the **smallest currency unit**. 
  For example, $15.24 should be sent as \`1524\`.
</Callout>

## Request

- **Method**: \`POST\`
- **URL**: \`\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/create\`

### Headers

| Header | Value | Description |
| :--- | :--- | :--- |
| \`Content-Type\` | \`application/json\` | Required for JSON payloads. |
| \`X-Api-Key\` | \`YOUR_API_KEY\` | Your unique API key. |

### Example

<Tabs items={['curl', 'Node.js', 'PHP', 'Laravel', 'Python', 'Rust', 'JSON']}>
  <Tabs.Tab>
    \`\`\`bash
    curl -X POST '\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/create' \\
      --header 'Accept: */*' \\
      --header 'Content-Type: application/json' \\
      --header 'X-Api-Key: YOUR_API_KEY' \\
      --data-raw '{
      "name": "Samir",
      "email": "email@example.com",
      "phone": "0501234567",
      "customerReferenceNumber": "3123123",
      "brokerReference": null,
      "callbackUrl": "https://webhook.site/353b2a68-6539-4405-8439-9d42327983eb?color=red",
      "amount": 1524,
      "expiresAt": null,
      "products": [
        {
          "title": "product 1",
          "details": "color: black",
          "amount": 10.3,
          "vat": 2,
          "quantity": 1,
          "total": 10.5
        }
      ],
      "metadata": {
        "businessLine": "20 - General Accident"
      }
    }'
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`javascript
    const response = await fetch('\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': 'YOUR_API_KEY'
      },
      body: JSON.stringify({
        name: "Samir",
        email: "email@example.com",
        phone: "0501234567",
        customerReferenceNumber: "3123123",
        amount: 1524,
        callbackUrl: "https://your-domain.com/webhook",
        products: [
          {
            title: "product 1",
            amount: 10.3,
            quantity: 1
          }
        ]
      })
    });

    const data = await response.json();
    console.log(data);
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`php
    $curl = curl_init();

    curl_setopt_array($curl, [
      CURLOPT_URL => "\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/create",
      CURLOPT_RETURNTRANSFER => true,
      CURLOPT_CUSTOMREQUEST => "POST",
      CURLOPT_POSTFIELDS => json_encode([
        "name" => "Samir",
        "email" => "email@example.com",
        "phone" => "0501234567",
        "customerReferenceNumber" => "3123123",
        "amount" => 1524,
        "callbackUrl" => "https://your-domain.com/webhook"
      ]),
      CURLOPT_HTTPHEADER => [
        "Content-Type: application/json",
        "X-Api-Key: YOUR_API_KEY"
      ],
    ]);

    $response = curl_exec($curl);
    curl_close($curl);
    echo $response;
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`php
    use Illuminate\\Support\\Facades\\Http;

    $response = Http::withHeaders([
        'X-Api-Key' => 'YOUR_API_KEY'
    ])->post('\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/create', [
        'name' => 'Samir',
        'email' => 'email@example.com',
        'phone' => '0501234567',
        'customerReferenceNumber' => '3123123',
        'amount' => 1524,
        'callbackUrl' => 'https://your-domain.com/webhook'
    ]);

    return $response->json();
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`python
    import requests
    import json

    url = "\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/create"

    payload = {
        "name": "Samir",
        "email": "email@example.com",
        "phone": "0501234567",
        "customerReferenceNumber": "3123123",
        "amount": 1524,
        "callbackUrl": "https://your-domain.com/webhook"
    }

    headers = {
        "Content-Type": "application/json",
        "X-Api-Key": "YOUR_API_KEY"
    }

    response = requests.post(url, json=payload, headers=headers)
    print(response.json())
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`rust
    use reqwest::header::{HeaderMap, HeaderValue, CONTENT_TYPE};
    use serde_json::json;

    #[tokio::main]
    async fn main() -> Result<(), Box<dyn std::error::Error>> {
        let client = reqwest::Client::new();
        let mut headers = HeaderMap::new();
        headers.insert("X-Api-Key", HeaderValue::from_static("YOUR_API_KEY"));
        headers.insert(CONTENT_TYPE, HeaderValue::from_static("application/json"));

        let payload = json!({
            "name": "Samir",
            "email": "email@example.com",
            "phone": "0501234567",
            "customerReferenceNumber": "3123123",
            "amount": 1524,
            "callbackUrl": "https://your-domain.com/webhook"
        });

        let res = client.post("\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/create")
            .headers(headers)
            .json(&payload)
            .send()
            .await?;

        println!("{:#?}", res.json::<serde_json::Value>().await?);
        Ok(())
    }
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`json
    {
      "name": "Samir",
      "email": "email@example.com",
      "phone": "0501234567",
      "customerReferenceNumber": "3123123",
      "brokerReference": null,
      "callbackUrl": "https://webhook.site/353b2a68-6539-4405-8439-9d42327983eb?color=red",
      "amount": 1524,
      "expiresAt": null,
      "products": [
        {
          "title": "product 1",
          "details": "color: black",
          "amount": 10.3,
          "vat": 2,
          "quantity": 1,
          "total": 10.5
        }
      ],
      "metadata": {
        "businessLine": "20 - General Accident"
      }
    }
    \`\`\`
  </Tabs.Tab>
</Tabs>

### Body Parameters

| Field | Type | Description |
| :--- | :--- | :--- |
| \`name\` | \`string\` | **Required**. Customer's full name. |
| \`email\` | \`string\` | **Required**. Customer's email address for sending the payment receipt. |
| \`amount\` | \`integer\` | **Required**. Total amount in the **smallest currency unit** (e.g., use \`1524\` for $15.24). |
| \`phone\` | \`string\` | Customer's phone number. |
| \`customerReferenceNumber\` | \`string\` | Your internal reference number for the customer or order. |
| \`brokerReference\` | \`string\` | Optional broker reference identifier. |
| \`callbackUrl\` | \`string\` | The URL our system will notify when the payment status changes. Must be **HTTPS** and publicly accessible. |
| \`expiresAt\` | \`string\` | Expiration date/time (e.g., \`YYYY-MM-DD HH:mm\`). If null, a default expiration period may apply. |
| \`products\` | \`array\` | List of items being purchased. Each item can include \`title\`, \`amount\`, \`quantity\`, etc. |
| \`metadata\` | \`object\` | A custom JSON object for your internal use. Any data sent here will be returned to you when querying the payment status or via webhooks. |

---

## Response

- **Status Code**: \`200 OK\`

### Response Headers

| Header | Description |
| :--- | :--- |
| \`Content-Type\` | \`application/json\` |
| \`X-Payd-Signature\` | \`t=...,v1=...\` Cryptographic HMAC-SHA256 signature for verifying response authenticity and data integrity. See [Signature Validation](/signatures). |

<Callout type="info">
  **Response Authenticity Guarantee**: Every PAYD API response includes an \`X-Payd-Signature\` header so your server can verify that the response originated from PAYD and was not altered in transit. Learn how to verify signatures in [Signature Validation](/signatures).
</Callout>

### Response Fields

| Field | Type | Description |
| :--- | :--- | :--- |
| \`status\` | \`string\` | The API request status (e.g., \`success\`). |
| \`message\` | \`string\` | A descriptive message about the result. |
| \`paymentLink\` | \`string\` | The URL the customer should visit to complete the payment. |
| \`invoiceId\` | \`string\` | The unique invoice identifier generated for this payment. |
| \`qrCode\` | \`string\` | A URL to a QR code image representing the payment link. |

### Body Example

\`\`\`json
{
  "status": "success",
  "message": "Payment link has been created successfully",
  "paymentLink": "https://business.payd.ae/pay/P677422619420838",
  "invoiceId": "P677422619420838",
  "qrCode": "https://business.payd.ae/qr-code/P677422619420838.png"
}
\`\`\`

### Notes

<Callout type="info">
  1. Always use an **HTTPS** URL in the \`callbackUrl\`.
  2. Ensure the \`callbackUrl\` is publicly accessible from the internet.
</Callout>
`,

  "get-payment-status": `# Get Payment Status

Retrieve the current lifecycle state and details of a payment request. 

## Request

- **Method**: \`GET\`
- **URL**: \`\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/status\`

### Headers

| Header | Value | Description |
| :--- | :--- | :--- |
| \`X-Api-Key\` | \`YOUR_API_KEY\` | Your unique API key for authentication. |

### Query Parameters

You must provide at least one identifier. **\`invoiceId\` is the preferred identifier** as it is guaranteed to be unique within the PAYD system.

| Field | Type | Description |
| :--- | :--- | :--- |
| \`invoiceId\` | \`string\` | **Preferred**. The unique invoice identifier returned during payment creation. |
| \`paymentId\` | \`string\` | The system-generated payment identifier. |
| \`customerReference\` | \`string\` | Your custom reference number provided during creation. |

---

## Response

- **Status Code**: \`200 OK\`

### Response Headers

| Header | Description |
| :--- | :--- |
| \`Content-Type\` | \`application/json\` |
| \`X-Payd-Signature\` | \`t=...,v1=...\` Cryptographic HMAC-SHA256 signature for verifying response authenticity and data integrity. See [Signature Validation](/signatures). |

<Callout type="info">
  **Response Authenticity Guarantee**: Every PAYD API response includes an \`X-Payd-Signature\` header so your server can verify that the response originated from PAYD and was not altered in transit. Learn how to verify signatures in [Signature Validation](/signatures).
</Callout>

### Field Reference

| Field | Type | Description |
| :--- | :--- | :--- |
| \`status\` | \`string\` | The API request status (e.g., \`success\`). |
| \`paymentStatus\` | \`string\` | The current lifecycle state of the payment. See [Status Meanings](#status-meanings) below. |
| \`invoiceId\` | \`string\` | The unique invoice identifier. |
| \`paymentId\` | \`string\` | The system-generated payment identifier (if available). |
| \`customerReference\`| \`string\` | Your custom reference number. |
| \`authCode\` | \`string|null\` | The authorization code from the payment processor (for successful payments). |
| \`cardNumber\` | \`string|null\` | The masked card number used for the transaction (e.g., \`411111******1111\`). |
| \`paidAt\` | \`string|null\` | The timestamp when the payment was completed (ISO 8601). |
| \`metadata\` | \`object|null\` | The custom JSON object provided during payment creation. |

### Body Example

\`\`\`json
{
  "status": "success",
  "paymentStatus": "completed",
  "invoiceId": "P522575121550499",
  "paymentId": "07075570983532404072",
  "customerReference": "abcx",
  "authCode": "123456",
  "cardNumber": "411111******1111",
  "paidAt": "2024-05-14T10:00:00Z",
  "metadata": null
}
\`\`\`

---

## Status Meanings

Understanding the \`paymentStatus\` field:

| Status | Description |
| :--- | :--- |
| \`pending\` | The payment has been initiated but is not yet completed. It is waiting for confirmation from the payment processor or customer action. |
| \`completed\` | The payment has been successfully processed and the transaction is finalized. |
| \`failed\` | The payment attempt was unsuccessful, typically due to insufficient funds, incorrect payment details, or network issues. |
| \`refunded\` | The payment was initially successful but has been refunded to the customer. |
| \`canceled\` | The payment was canceled by the customer or merchant before it was completed. |
| \`expired\` | The payment attempt was not completed within the allowed timeframe, and the session has expired. |
| \`on_hold\` | The payment is temporarily on hold, possibly due to a review process or an issue that requires further investigation before finalizing the transaction. |
| \`blocked\` | The payment link has been blocked due to suspicious activity. This status indicates that the system detected potential fraud or risk and prevented the transaction from proceeding. |
`,

  "cancel-payment": `# Cancel Payment

Cancel an existing payment request before it is completed. This is useful for voiding payment links that are no longer needed or were created in error.

<Callout type="info">
  Payment cancellation is only available for payments with a status of **pending** or **failed**. Completed or already canceled payments cannot be modified.
</Callout>

## Cancel payment by invoice ID

- **Method**: \`GET\`
- **URL**: \`\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/cancel\`

### Headers

| Header | Value | Description |
| :--- | :--- | :--- |
| \`X-Api-Key\` | \`YOUR_API_KEY\` | Your unique API key. |

### Query Parameters

| Field | Type | Description |
| :--- | :--- | :--- |
| \`invoiceId\` | \`string\` | **Required**. The unique invoice identifier (e.g., \`P522575121550499\`) of the payment to cancel. |

### Example

<Tabs items={['curl', 'Node.js', 'PHP', 'Laravel', 'Python', 'Rust']}>
  <Tabs.Tab>
    \`\`\`bash
    curl -X GET '\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/cancel?invoiceId=P522575121550499' \\
      --header 'X-Api-Key: YOUR_API_KEY'
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`javascript
    const response = await fetch('\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/cancel?invoiceId=P522575121550499', {
      method: 'GET',
      headers: {
        'X-Api-Key': 'YOUR_API_KEY'
      }
    });

    const data = await response.json();
    console.log(data);
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`php
    $curl = curl_init();

    curl_setopt_array($curl, [
      CURLOPT_URL => "\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/cancel?invoiceId=P522575121550499",
      CURLOPT_RETURNTRANSFER => true,
      CURLOPT_HTTPHEADER => [
        "X-Api-Key: YOUR_API_KEY"
      ],
    ]);

    $response = curl_exec($curl);
    curl_close($curl);
    echo $response;
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`php
    use Illuminate\\Support\\Facades\\Http;

    $response = Http::withHeaders([
        'X-Api-Key' => 'YOUR_API_KEY'
    ])->get('\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/cancel', [
        'invoiceId' => 'P522575121550499'
    ]);

    return $response->json();
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`python
    import requests

    url = "\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/cancel"
    params = {"invoiceId": "P522575121550499"}
    headers = {"X-Api-Key": "YOUR_API_KEY"}

    response = requests.get(url, params=params, headers=headers)
    print(response.json())
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`rust
    #[tokio::main]
    async fn main() -> Result<(), Box<dyn std::error::Error>> {
        let client = reqwest::Client::new();
        let res = client.get("\${REACT_APP_PAYD_API_BASE_URL}/api/v1/payment/cancel")
            .query(&[("invoiceId", "P522575121550499")])
            .header("X-Api-Key", "YOUR_API_KEY")
            .send()
            .await?;

        println!("{:#?}", res.json::<serde_json::Value>().await?);
        Ok(())
    }
    \`\`\`
  </Tabs.Tab>
</Tabs>

---

## Response

- **Status Code**: \`200 OK\`

### Response Headers

| Header | Description |
| :--- | :--- |
| \`Content-Type\` | \`application/json\` |
| \`X-Payd-Signature\` | \`t=...,v1=...\` Cryptographic HMAC-SHA256 signature for verifying response authenticity and data integrity. See [Signature Validation](/signatures). |

<Callout type="info">
  **Response Authenticity Guarantee**: Every PAYD API response includes an \`X-Payd-Signature\` header so your server can verify that the response originated from PAYD and was not altered in transit. Learn how to verify signatures in [Signature Validation](/signatures).
</Callout>

### Body Parameters

| Field | Type | Description |
| :--- | :--- | :--- |
| \`status\` | \`string\` | The API request status (e.g., \`success\`). |
| \`paymentStatus\` | \`string\` | The updated lifecycle state of the payment, which should now be \`canceled\`. |
| \`invoiceId\` | \`string\` | The invoice identifier of the canceled payment. |
| \`metadata\` | \`object|null\` | Any custom metadata that was associated with the payment. |

### Body Example

\`\`\`json
{
  "status": "success",
  "paymentStatus": "canceled",
  "invoiceId": "P522575121550499",
  "metadata": null
}
\`\`\`
`,

  refunds: `# Refunds

The Refunds API allows you to return funds to your customers for transactions that were previously completed.

## Create a Refund

To initiate a refund, send a \`POST\` request to the \`/refund/create\` endpoint.

### Endpoint

\`POST /api/v1/refund/create\`

### Request Body

| Parameter | Type | Required | Description |
| :--- | :--- | :--- | :--- |
| \`invoiceId\` | string | Yes | The unique identifier (UUID) of the payment to be refunded. |
| \`amount\` | numeric | Yes | The amount to refund. Must be less than or equal to the original payment amount. |
| \`remarks\` | string | No | Internal notes for the refund. |

### Example Request

\`\`\`json
{
  "invoiceId": "P123456789012345",
  "amount": 50.00,
  "remarks": "Customer requested a return"
}
\`\`\`

### Response

- **Headers**:
  - \`Content-Type: application/json\`
  - \`X-Payd-Signature: t=...,v1=...\` (HMAC-SHA256 signature for response authenticity verification. See [Signature Validation](/signatures))

\`\`\`json
{
  "status": "success",
  "message": "Refund request submitted successfully",
  "refundId": "R987654321098765"
}
\`\`\`

---

## Get Refund Status

Check the current status of a refund request.

### Endpoint

\`GET /api/v1/refund/status\`

### Query Parameters

| Parameter | Type | Required | Description |
| :--- | :--- | :--- | :--- |
| \`refundId\` | string | Yes | The unique identifier returned when the refund was created. |

### Example Request

\`GET /api/v1/refund/status?refundId=R987654321098765\`

### Response

\`\`\`json
{
  "status": "success",
  "refundStatus": "Completed",
  "amount": 50.00,
  "processedAt": "2026-05-14T10:30:00Z"
}
\`\`\`
`,

  webhook: `# Webhook Notification

Webhooks allow your system to receive real-time notifications about payment status changes. Instead of polling our API, we push notifications to your server.

## Recommended Flow

1. **Receive Webhook**: Our system sends a \`GET\` request to your \`callbackUrl\` with the \`invoiceId\`.
2. **Fetch Status**: Your server should then call our [Get Payment Status](/api/get-payment-status) API using that \`invoiceId\` to retrieve the latest, authenticated status and details.
3. **Update Database**: Use the information from the Status API to update your internal records.
4. **Acknowledge**: Respond to the webhook with a \`200 OK\` status.

---

## Webhook Request

- **Method**: \`GET\`
- **URL**: Uses the \`callbackUrl\` provided during payment link creation.

### Headers

| Header | Value | Description |
| :--- | :--- | :--- |
| \`Accept\` | \`application/json\` | Expects JSON or plain text response. |
| \`X-Payd-Signature\` | \`t=...,v1=...\` | Cryptographic HMAC-SHA256 signature header for verifying request authenticity. See [Signature Validation](/signatures). |

### URL Format

The webhook notification appends the \`invoiceId\` as a query parameter:
\`https://your-callback-url.com?invoiceId=P140003177497413\`

---

## Your Response

Your webhook handler must respond quickly and correctly to avoid unnecessary retries.

- **Status Code**: \`200 OK\`
- **Content Type**: \`application/json\` or \`text/plain\`

<Callout type="error">
  **Important**: Do not return HTML content or perform redirects in your webhook handler. Such responses will be treated as failures and will trigger the retry schedule.
</Callout>

### Receiver Examples

<Tabs items={['Node.js', 'PHP', 'Laravel', 'Python', 'Rust']}>
  <Tabs.Tab>
    \`\`\`javascript
    const express = require('express');
    const app = express();

    app.get('/webhook', (req, res) => {
      const { invoiceId } = req.query;
      console.log(\`Received webhook for invoice: \${invoiceId}\`);
      
      // 1. Fetch the latest status from our API using the invoiceId
      // 2. Update your database
      
      res.status(200).send('OK');
    });

    app.listen(3000, () => console.log('Webhook server running on port 3000'));
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`php
    // Simple PHP webhook receiver
    $invoiceId = $_GET['invoiceId'] ?? null;

    if ($invoiceId) {
        // Log the receipt
        error_log("Received webhook for invoice: " . $invoiceId);
        
        // 1. Fetch the latest status from our API using the invoiceId
        // 2. Update your database
        
        http_response_code(200);
        echo "OK";
    } else {
        http_response_code(400);
        echo "Missing invoiceId";
    }
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`php
    namespace App\\Http\\Controllers;

    use Illuminate\\Http\\Request;

    class WebhookController extends Controller
    {
        public function handle(Request $request)
        {
            $invoiceId = $request->query('invoiceId');
            
            \\Log::info("Received webhook for invoice: " . $invoiceId);
            
            // 1. Fetch the latest status from our API using the invoiceId
            // 2. Update your database
            
            return response('OK', 200);
        }
    }
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`python
    from flask import Flask, request

    app = Flask(__name__)

    @app.route('/webhook', methods=['GET'])
    def webhook():
        invoice_id = request.args.get('invoiceId')
        print(f"Received webhook for invoice: {invoice_id}")
        
        # 1. Fetch the latest status from our API using the invoice_id
        # 2. Update your database
        
        return "OK", 200

    if __name__ == '__main__':
        app.run(port=3000)
    \`\`\`
  </Tabs.Tab>
  <Tabs.Tab>
    \`\`\`rust
    use axum::{extract::Query, routing::get, Router};
    use serde::Deserialize;

    #[derive(Deserialize)]
    struct WebhookParams {
        invoiceId: String,
    }

    async fn handle_webhook(Query(params): Query<WebhookParams>) -> &'static str {
        println!("Received webhook for invoice: {}", params.invoiceId);
        
        // 1. Fetch the latest status from our API using the invoiceId
        // 2. Update your database
        
        "OK"
    }

    #[tokio::main]
    async fn main() {
        let app = Router::new().route("/webhook", get(handle_webhook));
        let listener = tokio::net::TcpListener::bind("0.0.0.0:3000").await.unwrap();
        axum::serve(listener, app).await.unwrap();
    }
    \`\`\`
  </Tabs.Tab>
</Tabs>

---

## Important Notes

1. **Successful Processing**: Respond with \`200 OK\` to confirm receipt. No further retries will occur.
2. **Error Handling**: If your server returns \`400\` or \`500\`, the system will resend the notification based on the retry schedule.
3. **Security**: Use a publicly accessible and secure (**HTTPS**) URL for receiving notifications.

## Retry Schedule

If the initial attempt fails, the system will retry up to **10 times**:

| Attempt | Delay After Previous |
| :--- | :--- |
| 1 | 10 seconds |
| 2 | 30 seconds |
| 3 | 2 minutes |
| 4 | 5 minutes |
| 5 | 10 minutes |
| 6 | 20 minutes |
| 7 | 30 minutes |
| 8 | 1 hour |
| 9 | 2 hours |
| 10 | 5 hours |
`,
};
